import React, { useEffect, useRef, useState } from 'react';
import { Download, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { fetchReportBlob } from '../services/reportsApi';
import { Button, Modal, Spinner } from './ui';
import '../assets/css/pdf-report-preview.css';

function getFullscreenElement() {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    msFullscreenElement?: Element | null;
  };
  return (
    doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.msFullscreenElement ||
    null
  );
}

async function requestElementFullscreen(element: HTMLElement) {
  const el = element as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
    msRequestFullscreen?: () => Promise<void> | void;
  };

  if (typeof el.requestFullscreen === 'function') {
    await el.requestFullscreen();
    return;
  }
  if (typeof el.webkitRequestFullscreen === 'function') {
    await el.webkitRequestFullscreen();
    return;
  }
  if (typeof el.msRequestFullscreen === 'function') {
    await el.msRequestFullscreen();
  }
}

async function exitElementFullscreen() {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    msExitFullscreen?: () => Promise<void> | void;
  };

  if (!getFullscreenElement()) return;

  if (typeof doc.exitFullscreen === 'function') {
    await doc.exitFullscreen();
    return;
  }
  if (typeof doc.webkitExitFullscreen === 'function') {
    await doc.webkitExitFullscreen();
    return;
  }
  if (typeof doc.msExitFullscreen === 'function') {
    await doc.msExitFullscreen();
  }
}

function buildPdfEmbedUrl(blobUrl: string) {
  return `${blobUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;
}

const PdfReportPreviewModal = ({
  show,
  onHide,
  title = 'Report Preview',
  projectId,
  reportItemId,
  file = null,
  blobUrl = null,
  fileName = 'report.pdf',
  onDownload = null,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ownedUrl, setOwnedUrl] = useState<string | null>(null);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const keepBlobAliveRef = useRef(false);

  useEffect(() => {
    const syncFullscreenState = () => {
      const active = getFullscreenElement();
      setIsNativeFullscreen(Boolean(active && stageRef.current && active === stageRef.current));
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    document.addEventListener('MSFullscreenChange', syncFullscreenState);

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
      document.removeEventListener('MSFullscreenChange', syncFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (!show && getFullscreenElement()) {
      exitElementFullscreen().catch(() => undefined);
      setIsNativeFullscreen(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    const loadPreview = async () => {
      setLoading(true);
      setError('');
      setPreviewUrl(null);

      try {
        if (blobUrl) {
          if (!cancelled) {
            setPreviewUrl(blobUrl);
          }
          return;
        }

        if (file instanceof Blob) {
          createdUrl = URL.createObjectURL(file);
          if (!cancelled) {
            setOwnedUrl(createdUrl);
            setPreviewUrl(createdUrl);
          }
          return;
        }

        if (projectId && reportItemId) {
          const blob = await fetchReportBlob(projectId, reportItemId);
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) {
            setOwnedUrl(createdUrl);
            setPreviewUrl(createdUrl);
          }
          return;
        }

        if (!cancelled) {
          setError('No PDF available to preview.');
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load PDF preview.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [show, projectId, reportItemId, file, blobUrl]);

  useEffect(() => {
    return () => {
      if (ownedUrl) {
        URL.revokeObjectURL(ownedUrl);
      }
    };
  }, [ownedUrl]);

  const openPdfOutsideModal = () => {
    if (!previewUrl) return false;
    const opened = window.open(previewUrl, '_blank', 'noopener,noreferrer');
    if (opened) {
      keepBlobAliveRef.current = true;
    }
    return Boolean(opened);
  };

  const handleEnterFullscreen = async () => {
    if (!previewUrl || !stageRef.current) return;

    try {
      await requestElementFullscreen(stageRef.current);
    } catch {
      if (!openPdfOutsideModal()) {
        setError('Unable to open full screen PDF preview in this browser.');
      }
    }
  };

  const handleExitFullscreen = async () => {
    try {
      await exitElementFullscreen();
    } catch {
      // User may have exited with Esc.
    }
    setIsNativeFullscreen(false);
  };

  const handleClose = async () => {
    try {
      await exitElementFullscreen();
    } catch {
      // Ignore.
    }

    if (ownedUrl && !keepBlobAliveRef.current) {
      URL.revokeObjectURL(ownedUrl);
    }
    setOwnedUrl(null);
    setPreviewUrl(null);
    setError('');
    setIsNativeFullscreen(false);
    onHide?.();
  };

  const handleDownloadClick = () => {
    if (typeof onDownload === 'function') {
      onDownload();
      return;
    }

    if (!previewUrl) return;

    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = fileName || 'report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const footerActions = (
    <div className="pdf-report-preview-actions">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!previewUrl}
        onClick={() => void handleEnterFullscreen()}
      >
        <Maximize2 className="pdf-report-preview-actions__icon" aria-hidden />
        Full screen
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!previewUrl}
        onClick={openPdfOutsideModal}
      >
        <ExternalLink className="pdf-report-preview-actions__icon" aria-hidden />
        Open in new tab
      </Button>
      <Button type="button" variant="secondary" size="sm" onClick={() => void handleClose()}>
        Close
      </Button>
      <Button
        type="button"
        variant="primary"
        size="sm"
        disabled={!previewUrl && typeof onDownload !== 'function'}
        onClick={handleDownloadClick}
      >
        <Download className="pdf-report-preview-actions__icon" aria-hidden />
        Download PDF
      </Button>
    </div>
  );

  return (
    <Modal
      open={show}
      onClose={() => void handleClose()}
      title={title}
      size="4xl"
      allowMaximize
      elevated
      className="pdf-report-preview-modal"
      footer={footerActions}
    >
      <div className="pdf-report-preview-panel">
        {loading ? (
          <div className="pdf-report-preview-state" role="status">
            <Spinner size="md" />
            <span>Loading preview…</span>
          </div>
        ) : error ? (
          <div className="pdf-report-preview-state pdf-report-preview-state--error">{error}</div>
        ) : previewUrl ? (
          <div
            ref={stageRef}
            className={`pdf-report-preview-stage${
              isNativeFullscreen ? ' pdf-report-preview-stage--native-fs' : ''
            }`}
          >
            {isNativeFullscreen ? (
              <div className="pdf-report-preview-fs-bar">
                <h2 className="pdf-report-preview-fs-bar__title app-heading-accent">{title}</h2>
                <div className="pdf-report-preview-fs-bar__actions">
                  <Button type="button" variant="outline" size="sm" onClick={handleDownloadClick}>
                    <Download className="pdf-report-preview-actions__icon" aria-hidden />
                    Download
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={openPdfOutsideModal}>
                    <ExternalLink className="pdf-report-preview-actions__icon" aria-hidden />
                    Open in new tab
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => void handleExitFullscreen()}
                  >
                    <Minimize2 className="pdf-report-preview-actions__icon" aria-hidden />
                    Exit full screen
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="pdf-report-preview-canvas">
              <iframe
                title={title}
                src={buildPdfEmbedUrl(previewUrl)}
                className="pdf-report-preview-frame"
              />
            </div>
          </div>
        ) : (
          <div className="pdf-report-preview-state">No preview available.</div>
        )}
      </div>
    </Modal>
  );
};

export default PdfReportPreviewModal;
